/**
 * Keeps whichever input a customer clicked visually selected within its
 * named group, even when Shopify's variant picker replaces its markup
 * after a change (assets/global.js VariantSelects -> assets/product-info.js
 * ProductInfo, which fully replaces the <variant-selects> subtree on every
 * option change rather than patching it) - AND across a genuine full page
 * reload or navigation, which the in-memory-only version of this file
 * could not do (confirmed by reproducing it: a fresh page load has no
 * memory of anything, so the server's own default - the first-rendered
 * label among any that share one option value - is shown/submitted
 * instead of whichever one the customer actually last picked).
 *
 * This file is deliberately generic: it has no knowledge of products,
 * variants, sizes, countries, or any other domain concept. It only tracks,
 * per product + section + input-group name, an identity for whichever
 * input was last chosen (see identityOf below), and re-applies that same
 * input if a match still exists after new markup lands - whether that
 * markup change came from Shopify's own AJAX re-render or from a fresh
 * page load. For a group where each value has exactly one input (the
 * ordinary case), this is a harmless no-op - Shopify's own rendering
 * already selects the right one. It only changes behavior for a group
 * where more than one input can share the same data-option-value-id, which
 * is not something this file needs to know about; it just preserves "the
 * thing that was clicked."
 *
 * Relies only on the pub/sub events assets/product-info.js already
 * publishes publicly (assets/pubsub.js, assets/constants.js) - nothing in
 * Shopify's own files is modified. The one thing this file publishes
 * itself (variantChange, only after actually correcting a stale selection
 * on initial load) is the exact same event Shopify's own code already
 * publishes after a real change - existing subscribers elsewhere
 * (snippets/localized-size-options.liquid's properties[Size] sync,
 * snippets/custom-size-selector.liquid's visible-picker sync) already know
 * how to correctly react to it; neither of those files needed to change.
 *
 * Why the persistence key is (product, section, input-group name, clicked
 * identity) and not just (section, input-group name):
 *
 * Confirmed by inspecting real rendered pages: the section id used here
 * (data-section on <variant-selects>) is derived from the product page's
 * TEMPLATE instance, not the individual product - several completely
 * different products on this store render the exact same section id when
 * they share a template. Scoping only by section+group would let a
 * selection made on one product leak onto an unrelated product that
 * happens to reuse the same template. The product id (data-product-id,
 * already present on the ancestor <product-info> element for every
 * product page - no new markup added for this) is included specifically
 * to prevent that; it is read generically (whatever the current page's
 * product happens to be), never hardcoded.
 *
 * Why the persistence key is (..., clicked identity) and not the
 * option-value ID:
 *
 * The option-value ID is never ambiguous by itself - Shopify's own re-render
 * always correctly re-selects an input carrying the right ID, because that's
 * exactly what "resolve the variant from option_values=<id>" means. Keying
 * on the ID would just be re-deriving information Shopify already gets
 * right on its own; it carries no information this file needs to add.
 *
 * The ambiguity this file exists to resolve lives one level below the ID:
 * several inputs can share one ID (e.g. "US 6" and "US 6.5" both resolving
 * to the same real variant). Those same inputs ALSO share `.value` in this
 * theme (both render with the real option's own raw value, e.g. "35") -
 * `.value` alone was originally assumed to distinguish them and does not;
 * that was a real bug (confirmed by reproducing it), fixed by preferring
 * data-display-label (unique per rendered input wherever a group sets it)
 * over `.value` as the remembered identity - see identityOf. Groups that
 * never set data-display-label (e.g. Color) are completely unaffected,
 * since identityOf falls back to `.value` for them exactly as before.
 *
 * sessionStorage specifics: wrapped defensively throughout (private
 * browsing/disabled storage, quota errors, corrupted JSON from a stale
 * previous version of this file, etc. all degrade to "behave as if nothing
 * was ever remembered" rather than throwing). Chosen over localStorage
 * deliberately: it survives exactly "refresh" and "navigate away and
 * return within the same tab" (what this fix targets) while not
 * persisting indefinitely across unrelated later visits.
 */
(() => {
  if (window.__preserveInputSelectionLoaded) return;
  window.__preserveInputSelectionLoaded = true;

  const STORAGE_KEY = 'preserve-input-selection:v1';

  // Confirms sessionStorage is not just present but actually usable (some
  // browsers expose window.sessionStorage yet throw on any read/write in
  // certain private-browsing modes) - probed once, up front, rather than
  // wrapping every later call site in its own try/catch.
  function getUsableSessionStorage() {
    try {
      const storage = window.sessionStorage;
      const probeKey = '__preserve_input_selection_probe__';
      storage.setItem(probeKey, '1');
      storage.removeItem(probeKey);
      return storage;
    } catch (error) {
      return null;
    }
  }

  const storage = getUsableSessionStorage();

  function loadPersisted() {
    if (!storage) return {};
    try {
      const raw = storage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

      // Discards anything that isn't a plain string -> string entry, so a
      // corrupted or unexpectedly-shaped stored value (e.g. left over from
      // a future/different version of this file) can never cause a bad
      // identity to be applied - it's simply dropped, same as if it had
      // never been stored.
      const clean = {};
      Object.keys(parsed).forEach((key) => {
        if (typeof parsed[key] === 'string') clean[key] = parsed[key];
      });
      return clean;
    } catch (error) {
      return {};
    }
  }

  function persist() {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(lastSelectedValueByGroup));
    } catch (error) {
      // Quota exceeded, storage revoked mid-session, etc. - the in-memory
      // map (still correct for this page load) is left untouched; only
      // cross-page-load persistence for this one write is skipped.
    }
  }

  const lastSelectedValueByGroup = loadPersisted();

  // Generic on purpose - reads whichever product id the current page
  // already exposes (data-product-id on the ancestor <product-info>
  // element, present for every product page without any change needed
  // here), never a specific product/handle.
  function productIdFor(element) {
    return element?.closest('product-info')?.dataset.productId ?? '';
  }

  const groupKey = (productId, sectionId, groupName) => `${productId}::${sectionId}::${groupName}`;

  const groupBy = (elements, keyFn) =>
    Array.from(elements).reduce((groups, element) => {
      const key = keyFn(element);
      (groups[key] ||= []).push(element);
      return groups;
    }, {});

  function rememberSelection({ data: { target } }) {
    const variantSelects = target.closest('variant-selects');
    const sectionId = variantSelects?.dataset.section;
    const groupName = target.name || target.closest('select')?.name;
    if (!sectionId || !groupName) return;

    const productId = productIdFor(variantSelects);
    lastSelectedValueByGroup[groupKey(productId, sectionId, groupName)] = identityOf(target);
    persist();
  }

  // Some groups intentionally render more than one input sharing the exact
  // same `value` (e.g. two customer-facing aliases - "US 4" and "US 4.5" -
  // both pointing at one real option value): `.value` alone can't tell
  // those apart, which is exactly the ambiguity this file exists to
  // resolve, so `.value` alone is not a safe identity for this file's own
  // stated purpose. data-display-label, when a group sets it, is unique
  // per rendered input; falling back to `.value` when it's absent keeps
  // this identical to the previous behavior for every group that doesn't
  // use it (e.g. Color, whose inputs never set data-display-label at all).
  function identityOf(input) {
    return input.dataset.displayLabel ?? input.value;
  }

  function restoreSelection({ data: { sectionId } }) {
    const variantSelects = document.querySelector(`variant-selects[data-section="${sectionId}"]`);
    if (!variantSelects) return;

    restoreWithinVariantSelects(variantSelects);
  }

  // Shared by both the AJAX-re-render path (restoreSelection, subscribed
  // to variantChange below) and the fresh-page-load path (restoreAllOnLoad
  // further down) - one implementation, two entry points, so a fresh load
  // is corrected using the exact same logic already proven for re-renders.
  // Returns whether anything was actually changed, so callers only publish
  // variantChange when there is a real correction to announce.
  function restoreWithinVariantSelects(variantSelects) {
    const sectionId = variantSelects.dataset.section;
    const productId = productIdFor(variantSelects);
    let didRestoreAnything = false;

    const radioGroups = groupBy(variantSelects.querySelectorAll('input[type="radio"][name]'), (el) => el.name);
    Object.entries(radioGroups).forEach(([name, radios]) => {
      if (restoreGroup(productId, sectionId, name, radios)) didRestoreAnything = true;
    });

    variantSelects.querySelectorAll('select[name]').forEach((select) => {
      if (restoreGroup(productId, sectionId, select.name, Array.from(select.options))) didRestoreAnything = true;
    });

    return didRestoreAnything;
  }

  function restoreGroup(productId, sectionId, groupName, candidates) {
    const remembered = lastSelectedValueByGroup[groupKey(productId, sectionId, groupName)];
    if (remembered === undefined) return false;

    const match = candidates.find((candidate) => identityOf(candidate) === remembered);
    if (!match) return false;

    applySelection(match, candidates);
    return true;
  }

  function applySelection(input, candidates) {
    if (input.tagName === 'OPTION') {
      const select = input.closest('select');
      if (!select) return;

      select.querySelectorAll('option[selected]').forEach((option) => option.removeAttribute('selected'));
      input.setAttribute('selected', 'selected');
      select.value = input.value;
      return;
    }

    // Explicitly uncheck every other candidate in this same group rather
    // than relying on native radio-group auto-uncheck: these inputs use
    // form="..." (not DOM nesting inside a <form>) to associate with the
    // product form, and radio-group scoping by form owner isn't
    // consistently implemented everywhere (the same reasoning already
    // applied in snippets/custom-size-selector.liquid's forwardToRealInput)
    // - being explicit removes any doubt that exactly one candidate ends
    // up checked.
    candidates.forEach((candidate) => {
      if (candidate !== input && candidate.tagName !== 'OPTION') candidate.checked = false;
    });
    input.checked = true;

    const legend = input.closest('.product-form__input')?.querySelector('[data-selected-value]');
    if (legend) legend.textContent = input.value;
  }

  // Runs once, immediately (this script is loaded with `defer`, positioned
  // before assets/global.js in layout/theme.liquid, so the server-rendered
  // DOM already exists and Shopify's own VariantSelects/ProductInfo custom
  // elements have not been defined/upgraded yet - this correction lands
  // before anything else ever inspects the initial checked state, so there
  // is nothing to race against). Covers exactly the two scenarios the
  // AJAX-only version could not: a full page refresh, and navigating away
  // and back. Publishing variantChange only when a real correction was
  // applied (not on every load) is what lets the existing, unmodified
  // subscribers in localized-size-options.liquid and
  // custom-size-selector.liquid pick up the fix - and is also what keeps
  // this from ever calling back into rememberSelection (that only listens
  // for optionValueSelectionChange, which nothing here publishes), so
  // there is no loop: at most, this file's own restoreSelection also
  // receives the variantChange it just published and re-checks an already-
  // matching state, which is a harmless idempotent no-op, not a cycle.
  function restoreAllOnLoad() {
    document.querySelectorAll('variant-selects[data-section]').forEach((variantSelects) => {
      const didRestoreAnything = restoreWithinVariantSelects(variantSelects);
      if (!didRestoreAnything) return;

      const sectionId = variantSelects.dataset.section;
      if (!sectionId) return;
      publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId } });
    });
  }

  subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, rememberSelection);
  subscribe(PUB_SUB_EVENTS.variantChange, restoreSelection);
  restoreAllOnLoad();
})();
